import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Form, Formik } from 'formik';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import { RoomPropertiesListAddMutation } from '__generated__/RoomPropertiesListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const RoomPropertyListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<RoomPropertiesListAddMutation>(graphql`
    mutation RoomPropertiesListAddMutation(
      $input: CreateRoomPropertyInput!
      $connections: [ID!]!
    ) {
      createRoomProperty(input: $input) {
        node
          @prependNode(
            connections: $connections
            edgeTypeName: "RoomPropertyEdge"
          ) {
          ...RoomPropertiesListRow_roomProperty
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionId], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionId, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Nauja kambario savybė</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują kambario savybę.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default RoomPropertyListAdd;
